import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import type {
  MemphisAssetAtRisk,
  MemphisAssetOperationalStatus,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/** Stable id for the hardcoded Equinor Grane oil-spill inbox item. */
export const EQUINOR_GRANE_THREAT_NOTIFICATION_ID =
  'demo-equinor-grane-oil-spill';

export const EQUINOR_GRANE_ASSETS_LAYER_ID = 'equinor-grane-assets';
export const EQUINOR_GRANE_ASSETS_LAYER_TITLE = 'Equinor Grane assets';

export const EQUINOR_OIL_SLICK_LAYER_ID = 'equinor-oil-slick';
export const EQUINOR_OIL_SLICK_LAYER_TITLE = 'Grane release — surface slick';

/**
 * Synthetic slick for the demo — there is no live feed behind it. The main
 * plume runs east from Grane on the prevailing drift, and the nearshore lobe is
 * the heavier oiling the script puts on Utsira's west and north coasts.
 */
export const EQUINOR_SLICK_PLUME_RING: number[][] = [
  [2.42, 59.2],
  [2.75, 59.24],
  [3.2, 59.28],
  [3.7, 59.31],
  [4.2, 59.34],
  [4.6, 59.36],
  [4.82, 59.35],
  [4.86, 59.31],
  [4.7, 59.28],
  [4.3, 59.26],
  [3.8, 59.23],
  [3.3, 59.19],
  [2.85, 59.15],
  [2.5, 59.13],
  [2.42, 59.2],
];

export const EQUINOR_SLICK_NEARSHORE_RING: number[][] = [
  [4.83, 59.29],
  [4.82, 59.325],
  [4.845, 59.338],
  [4.9, 59.342],
  [4.925, 59.335],
  [4.921, 59.325],
  [4.888, 59.328],
  [4.862, 59.322],
  [4.856, 59.3],
  [4.858, 59.292],
  [4.83, 59.29],
];

export type EquinorSlickPolygon = {
  id: string;
  name: string;
  description: string;
  polygonColor: string;
  polygonOpacity: number;
  ring: number[][];
};

export const EQUINOR_SLICK_POLYGONS: EquinorSlickPolygon[] = [
  {
    id: 'equinor-slick-plume',
    name: 'Grane release — surface slick',
    description:
      'Heavy crude on the surface, drifting east from the Grane Facility toward Utsira.',
    polygonColor: '#231508',
    polygonOpacity: 0.55,
    ring: EQUINOR_SLICK_PLUME_RING,
  },
  {
    id: 'equinor-slick-nearshore',
    name: 'Utsira shoreline oiling',
    description:
      'Heavy oiling on the west and north coasts of Utsira, including the main harbour.',
    polygonColor: '#140c06',
    polygonOpacity: 0.75,
    ring: EQUINOR_SLICK_NEARSHORE_RING,
  },
];

const EQUINOR_GRANE_THREAT_SENT_AT = '2026-09-09T06:00:00.000Z';

export const EQUINOR_GRANE_EVENT_STARTED_AT = '2026-09-09T03:00:00.000Z';

export const EQUINOR_GRANE_THREAT_PROVENANCE =
  '7 Assets Impacted by Uncontrolled Crude Release per Grane Facility Operations';

export const EQUINOR_GRANE_THRESHOLD_DESCRIPTION =
  'Uncontrolled hydrocarbon release threshold exceeded — heavy crude reaching open water from the Grane Facility';

export const EQUINOR_GRANE_EVENT_REGION = 'North Sea — Norwegian sector';

export const EQUINOR_GRANE_SPILL_RULE_NAME =
  'Equinor Grane — uncontrolled hydrocarbon release';
export const EQUINOR_SHORELINE_RULE_NAME = 'Equinor Grane — shoreline impact';
export const EQUINOR_MARINE_RECOVERY_RULE_NAME =
  'Equinor Grane — marine recovery';
export const EQUINOR_LOGISTICS_RULE_NAME = 'Equinor Grane — response logistics';

export const EQUINOR_THREAT_TO_RULE_NAME: Record<string, string> = {
  'Oil Spill': EQUINOR_GRANE_SPILL_RULE_NAME,
  'Shoreline Impact': EQUINOR_SHORELINE_RULE_NAME,
  'Marine Recovery': EQUINOR_MARINE_RECOVERY_RULE_NAME,
  Logistics: EQUINOR_LOGISTICS_RULE_NAME,
};

export const EQUINOR_GRANE_EVENT_SUMMARY =
  'The Grane Facility is releasing heavy crude to open water. Sea state is rough, so offshore recovery is not possible for several days and the slick is driving east toward shore. Oil has washed onto the west and north coasts of Utsira, including the sheltered main harbour serving Sjoarskogen.';

export const EQUINOR_GRANE_LINKED_EVENT_NAME =
  'Oil Spill — Grane Facility uncontrolled release, 7 assets impacted';

export const EQUINOR_GRANE_RESOURCES_COMMITTED =
  '7 assets are inside the release and shoreline impact area';

export const EQUINOR_GRANE_ORG_EVENT_DEMO_KEY = 'equinor-grane-spill';

export const EQUINOR_GRANE_RESOURCE_NOTES_PREFIX = 'equinor-grane:';

export const EQUINOR_GRANE_SYMBOLOGY = 'facility';

export const EQUINOR_ASSET_COA_NOTIFY_STORAGE_KEY =
  'equinor-grane-asset-coa-notify-v1';

export const EQUINOR_SPILL_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'equinor-checklist-accountability',
    kind: 'send_notification',
    label: 'Send accountability check notification',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate:
        'Accountability check: Grane Facility personnel — {eventName}',
      bodyTemplate:
        'Confirm personnel accountability on the Grane Facility and supporting vessels for {eventName}. Report status in PRATUS.',
      emailSubject: 'Accountability check: Grane Facility — {eventName}',
      emailMessage:
        'Confirm personnel accountability on the Grane Facility and supporting vessels. Review Courses of Action and impacted assets in PRATUS.',
      appMessage:
        'Accountability check: {eventName} — confirm personnel status on Grane and supporting vessels.',
    },
  },
  {
    id: 'equinor-checklist-source-control',
    kind: 'org_action',
    label: 'Establish source control and confirm release rate',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-checklist-regulatory-notification',
    kind: 'send_notification',
    label: 'Notify Norwegian Coastal Administration',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Regulatory notification — {eventName}',
      bodyTemplate:
        'Report the uncontrolled release, current release rate and forecast slick trajectory for {eventName} to the Norwegian Coastal Administration.',
      emailSubject: 'Regulatory notification — {eventName}',
      emailMessage:
        'Report release rate, estimated volume and forecast trajectory to the Norwegian Coastal Administration. Attach the current common operating picture from PRATUS.',
      appMessage:
        'Regulatory notification sent for {eventName} — release rate and trajectory reported.',
    },
  },
  {
    id: 'equinor-checklist-volume-estimate',
    kind: 'org_action',
    label: 'Estimate total released volume and forecast trajectory',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const EQUINOR_SHORELINE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'equinor-shoreline-assessment',
    kind: 'org_action',
    label: 'Run shoreline assessment of oiled Utsira coastline',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-shoreline-protect-harbour',
    kind: 'org_action',
    label: 'Boom and protect the sheltered main harbour',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-shoreline-wildlife',
    kind: 'org_action',
    label: 'Stand up wildlife response for oiled shoreline',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-shoreline-community-notify',
    kind: 'send_notification',
    label: 'Notify Sjoarskogen residents of shoreline impact',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Shoreline impact advisory — {eventName}',
      bodyTemplate:
        'Oil has reached the west and north coasts of Utsira. Keep clear of the oiled shoreline and the main harbour until recovery crews clear the area.',
      emailSubject: 'Shoreline impact advisory — {eventName}',
      emailMessage:
        'Oil has washed ashore on the west and north coasts of Utsira, including the main harbour. Avoid the affected shoreline. Tuvevagen remains open.',
      appMessage:
        'Shoreline impact advisory: avoid the oiled Utsira coastline and main harbour.',
    },
  },
];

export const EQUINOR_MARINE_RECOVERY_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] =
  [
    {
      id: 'equinor-recovery-launch-vessels',
      kind: 'org_action',
      label: 'Launch recovery vessels once sea state permits',
      orgAction: { status: 'Planned', poc: null },
    },
    {
      id: 'equinor-recovery-boom-deployment',
      kind: 'org_action',
      label: 'Deploy containment boom at recoverable locations',
      orgAction: { status: 'Planned', poc: null },
    },
    {
      id: 'equinor-recovery-sea-state-notify',
      kind: 'send_notification',
      label: 'Notify response fleet that recovery has started',
      notify: {
        notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
        priority: 'watch',
        titleTemplate: 'Recovery window open — {eventName}',
        bodyTemplate:
          'Sea state has moderated for {eventName}. Begin surface recovery at the sheltered main harbour and at suitable exposed locations only.',
        emailSubject: 'Recovery window open — {eventName}',
        emailMessage:
          'Sea state has moderated. Begin surface recovery at the sheltered main harbour. Exposed coastline is not suitable for safe recovery except at identified locations.',
        appMessage:
          'Recovery window open for {eventName} — begin surface recovery at sheltered locations.',
      },
    },
  ];

export const EQUINOR_LOGISTICS_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'equinor-logistics-landing-site',
    kind: 'org_action',
    label: 'Stand up Tuvevagen as the resource landing site',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-logistics-waste-handling',
    kind: 'org_action',
    label: 'Arrange recovered oil offload and waste handling',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'equinor-logistics-supply-base-notify',
    kind: 'send_notification',
    label: 'Notify supply base of resource movement',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'watch',
      titleTemplate: 'Resource movement — {eventName}',
      bodyTemplate:
        'Route response resources for {eventName} through Tuvevagen on the south side of Utsira. The main harbour is oiled and reserved for recovery.',
      emailSubject: 'Resource movement — {eventName}',
      emailMessage:
        'Route response resources through Tuvevagen. Confirm offload capacity for recovered oil and stage from the Stavanger supply base.',
      appMessage:
        'Resource movement for {eventName}: land through Tuvevagen, not the main harbour.',
    },
  },
];

export const EQUINOR_SPILL_CHECKLIST_ORG_ACTION_LABELS =
  EQUINOR_SPILL_CHECKLIST_ITEMS.filter(
    (item) => item.kind === 'org_action'
  ).map((item) => item.label);

export function isEquinorGraneOrgActionName(
  name: string | null | undefined
): boolean {
  return !!name && EQUINOR_SPILL_CHECKLIST_ORG_ACTION_LABELS.includes(name);
}

const OIL_SPILL_THREAT = 'Oil Spill';
const SHORELINE_THREAT = 'Shoreline Impact';
const MARINE_RECOVERY_THREAT = 'Marine Recovery';
const LOGISTICS_THREAT = 'Logistics';

export function equinorChecklistsForThreats(
  threats: string[]
): Record<string, OrgEventChecklistItemDef[]> {
  const checklists: Record<string, OrgEventChecklistItemDef[]> = {};
  for (const threat of threats) {
    if (threat === OIL_SPILL_THREAT) {
      checklists[threat] = EQUINOR_SPILL_CHECKLIST_ITEMS;
    } else if (threat === SHORELINE_THREAT) {
      checklists[threat] = EQUINOR_SHORELINE_CHECKLIST_ITEMS;
    } else if (threat === MARINE_RECOVERY_THREAT) {
      checklists[threat] = EQUINOR_MARINE_RECOVERY_CHECKLIST_ITEMS;
    } else if (threat === LOGISTICS_THREAT) {
      checklists[threat] = EQUINOR_LOGISTICS_CHECKLIST_ITEMS;
    }
  }
  return checklists;
}

export function equinorChecklistsForAutomationRules(threats: string[]): {
  automationRules: string[];
  automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]>;
} {
  const threatChecklists = equinorChecklistsForThreats(threats);
  const automationRules: string[] = [];
  const automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]> =
    {};
  for (const threat of threats) {
    const ruleName = EQUINOR_THREAT_TO_RULE_NAME[threat];
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    const items = threatChecklists[threat];
    if (items) automationRuleChecklists[ruleName] = items;
  }
  return { automationRules, automationRuleChecklists };
}

/**
 * Same field names as the DHS facility so both demos share one render path.
 * `state` carries the Norwegian county, or the sector for offshore assets.
 */
export type EquinorGraneFacility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

/**
 * The seven locations the scenario script names, in the order the story runs:
 * the offshore source, then the shoreline it reaches, then the clean harbour
 * the response stages through.
 */
export const EQUINOR_GRANE_FACILITIES: EquinorGraneFacility[] = [
  {
    id: 'equinor-grane-platform',
    name: 'Grane Facility',
    city: 'North Sea',
    state: 'Block 25/11',
    lon: 2.4928,
    lat: 59.1644,
  },
  {
    id: 'equinor-svalin-tieback',
    name: 'Svalin Subsea Tieback',
    city: 'North Sea',
    state: 'Block 25/11',
    lon: 2.44,
    lat: 59.12,
  },
  {
    id: 'equinor-utsira-sjoarskogen',
    name: 'Sjoarskogen',
    city: 'Utsira',
    state: 'Rogaland',
    lon: 4.8783,
    lat: 59.3072,
  },
  {
    id: 'equinor-utsira-main-harbour',
    name: 'Utsira Main Harbour',
    city: 'Utsira',
    state: 'Rogaland',
    lon: 4.876,
    lat: 59.313,
  },
  {
    id: 'equinor-utsira-west-coast',
    name: 'Utsira West Coastline',
    city: 'Utsira',
    state: 'Rogaland',
    lon: 4.868,
    lat: 59.308,
  },
  {
    id: 'equinor-utsira-north-coast',
    name: 'Utsira North Coastline',
    city: 'Utsira',
    state: 'Rogaland',
    lon: 4.883,
    lat: 59.315,
  },
  {
    id: 'equinor-utsira-tuvevagen',
    name: 'Tuvevagen Harbour',
    city: 'Utsira',
    state: 'Rogaland',
    lon: 4.887,
    lat: 59.302,
  },
];

export type EquinorAssetsTabSeed = EquinorGraneFacility & {
  threats: string[];
  inImpactArea?: boolean;
};

const IMPACT_AREA_EXTRA_THREATS: Record<string, string[]> = {
  'equinor-grane-platform': [MARINE_RECOVERY_THREAT],
  'equinor-svalin-tieback': [MARINE_RECOVERY_THREAT],
  'equinor-utsira-sjoarskogen': [SHORELINE_THREAT, LOGISTICS_THREAT],
  'equinor-utsira-main-harbour': [SHORELINE_THREAT, MARINE_RECOVERY_THREAT],
  'equinor-utsira-west-coast': [SHORELINE_THREAT],
  'equinor-utsira-north-coast': [SHORELINE_THREAT],
  'equinor-utsira-tuvevagen': [LOGISTICS_THREAT],
};

/** Supporting assets that carry the response but sit outside the oiled area. */
export const EQUINOR_ASSETS_TAB_EXTRA_FACILITIES: EquinorAssetsTabSeed[] = [
  {
    id: 'equinor-stavanger-supply-base',
    name: 'Stavanger Supply Base',
    city: 'Stavanger',
    state: 'Rogaland',
    lon: 5.66,
    lat: 58.988,
    threats: [LOGISTICS_THREAT],
  },
  {
    id: 'equinor-haugesund-shore-base',
    name: 'Haugesund Shore Base',
    city: 'Haugesund',
    state: 'Rogaland',
    lon: 5.268,
    lat: 59.4136,
    threats: [LOGISTICS_THREAT],
  },
  {
    id: 'equinor-sture-terminal',
    name: 'Sture Terminal',
    city: 'Oygarden',
    state: 'Vestland',
    lon: 4.85,
    lat: 60.62,
    threats: [LOGISTICS_THREAT],
  },
  {
    id: 'equinor-osrv-grane',
    name: 'Recovery Vessel — Grane Standby',
    city: 'North Sea',
    state: 'On station',
    lon: 2.75,
    lat: 59.19,
    threats: [MARINE_RECOVERY_THREAT],
  },
  {
    id: 'equinor-osrv-transit',
    name: 'Recovery Vessel — Mid-field',
    city: 'North Sea',
    state: 'On station',
    lon: 3.8,
    lat: 59.25,
    threats: [MARINE_RECOVERY_THREAT],
  },
  {
    id: 'equinor-osrv-utsira',
    name: 'Recovery Vessel — Utsira Approach',
    city: 'North Sea',
    state: 'On station',
    lon: 4.7,
    lat: 59.31,
    threats: [MARINE_RECOVERY_THREAT],
  },
];

export const EQUINOR_ASSETS_TAB_SEEDS: EquinorAssetsTabSeed[] = [
  ...EQUINOR_GRANE_FACILITIES.map((facility) => ({
    ...facility,
    inImpactArea: true,
    threats: [
      OIL_SPILL_THREAT,
      ...(IMPACT_AREA_EXTRA_THREATS[facility.id] ?? []),
    ],
  })),
  ...EQUINOR_ASSETS_TAB_EXTRA_FACILITIES,
];

export function formatEquinorFacilityLabel(
  facility: EquinorGraneFacility
): string {
  return `${facility.name} (${facility.city}, ${facility.state})`;
}

export function equinorGraneResourceNotesMarker(facilityId: string): string {
  return `${EQUINOR_GRANE_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function equinorFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(EQUINOR_GRANE_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(EQUINOR_GRANE_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isEquinorGraneResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(EQUINOR_GRANE_RESOURCE_NOTES_PREFIX);
}

/**
 * Each impacted location plays a different role in the script, so the risk line
 * is written per asset rather than filled from one template.
 */
const ASSET_RISK_BY_ID: Record<
  string,
  { risk: string; operationalStatus: MemphisAssetOperationalStatus }
> = {
  'equinor-grane-platform': {
    risk: 'Source of the release. Heavy crude is flowing to open water at a restricted but uncontrolled rate. Source control is not expected to be complete for three days and the total spill volume is unknown.',
    operationalStatus: 'Not Operational',
  },
  'equinor-svalin-tieback': {
    risk: 'Tied back to Grane and shut in alongside the platform while source control work continues. No separate release path identified.',
    operationalStatus: 'Not Operational',
  },
  'equinor-utsira-sjoarskogen': {
    risk: 'The town is being immediately affected. Its main harbour and the coastline either side of it are oiled, cutting off normal harbour use for residents.',
    operationalStatus: 'Partially Operational',
  },
  'equinor-utsira-main-harbour': {
    risk: 'Extensively covered in slick, but sheltered, which makes it the one impacted location suitable for both shoreline and water-surface recovery. Reserve it for recovery rather than resource landing.',
    operationalStatus: 'Not Operational',
  },
  'equinor-utsira-west-coast': {
    risk: 'Extensive oiling along exposed coastline. Sea state and exposure make most of this stretch unsafe for recovery work; respond only at identified suitable locations.',
    operationalStatus: 'Not Operational',
  },
  'equinor-utsira-north-coast': {
    risk: 'Extensive oiling along exposed coastline. As with the west coast, treat as assessment-only except where a safe access point has been confirmed.',
    operationalStatus: 'Not Operational',
  },
  'equinor-utsira-tuvevagen': {
    risk: 'South facing and untouched by the slick, and not expected to be affected. Use it to land response resources and to remove recovered oil from the island.',
    operationalStatus: 'Operational',
  },
};

function buildAssetFromFacility(
  facility: EquinorGraneFacility
): MemphisAssetAtRisk {
  const detail = ASSET_RISK_BY_ID[facility.id];
  return {
    id: facility.id,
    resource: formatEquinorFacilityLabel(facility),
    risk:
      detail?.risk ??
      `${formatEquinorFacilityLabel(facility)} sits inside the Grane release impact area. Confirm site status and report in PRATUS.`,
    location: [facility.lon, facility.lat],
    operationalStatus: detail?.operationalStatus ?? 'Partially Operational',
  };
}

export function getEquinorRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'North Sea — Grane release and Utsira shoreline',
    description:
      'An uncontrolled heavy crude release from the Grane Facility is driving east toward shore, placing the following Equinor assets and Utsira locations at elevated risk:',
    threats: EQUINOR_GRANE_FACILITIES.map(buildAssetFromFacility),
    responseChecklist: EQUINOR_SPILL_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedEquinorGraneThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === EQUINOR_GRANE_THREAT_NOTIFICATION_ID;
}

export function getEquinorGraneThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: EQUINOR_GRANE_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: EQUINOR_GRANE_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: EQUINOR_GRANE_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'Uncontrolled heavy crude release from the Grane Facility. Slick driving east with oil ashore on Utsira.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: EQUINOR_GRANE_THREAT_PROVENANCE,
  };
}
